import { z } from 'zod';

import type { UserConfidenceRange } from '../../../utilities';

const confidenceBoundSchema = z.coerce
  .number({ invalid_type_error: 'Must be a number' })
  .min(0, 'Cannot be less than 0')
  .max(1, 'Cannot be greater than 1');

export const userConfidenceRangeSchema = z
  .object({
    min: confidenceBoundSchema,
    max: confidenceBoundSchema,
    template: z.string(),
  })
  .refine(({ min, max }) => min < max, {
    message: 'Min must be less than Max',
    path: ['min'],
  });

export const getUserConfidenceRangeFieldErrors = (
  item: Partial<UserConfidenceRange>
): Partial<Record<keyof UserConfidenceRange, string>> => {
  const result = userConfidenceRangeSchema.safeParse(item);
  if (result.success) return {};

  return Object.fromEntries(
    result.error.errors.map(err => [err.path[0], err.message])
  ) as Partial<Record<keyof UserConfidenceRange, string>>;
};

export const validateUserConfidenceRanges = (
  value?: UserConfidenceRange[] | null
): string | false => {
  if (!value || value.length === 0) return false;

  const itemsResult = z.array(userConfidenceRangeSchema).safeParse(value);
  if (!itemsResult.success) {
    return itemsResult.error.errors
      .map(err => {
        const [index, field] = err.path;
        return `Range #${Number(index) + 1} (${field}): ${err.message}`;
      })
      .join(', ');
  }

  const total = value.reduce(
    (sum, { min, max }) => sum + (Number(max) - Number(min)),
    0
  );
  if (Math.abs(total - 1) >= 1e-6) {
    return `Confidence ranges must span exactly 1.0 — current sum: ${total.toFixed(4)} (sum of max − min for all ranges)`;
  }

  return false;
};
